import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItemIcon from '@mui/material/ListItemIcon'
import MenuItem from '@mui/material/MenuItem'
import Popover from '@mui/material/Popover'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import { useColorScheme, useTheme } from '@mui/material/styles'
import { LockKey as LockKeyIcon } from '@phosphor-icons/react/dist/ssr/LockKey'
import { Moon as MoonIcon } from '@phosphor-icons/react/dist/ssr/Moon'
import { User as UserIcon } from '@phosphor-icons/react/dist/ssr/User'
import { Link as RouterLink } from 'react-router-dom'

import { config } from '@/config'
import { paths } from '@/paths'
import { AuthStrategy } from '@/lib/auth/strategy'
import { setSettings as setPersistedSettings } from '@/lib/settings/set-settings'
import { useSettings } from '@/hooks/use-settings'
import { useUser } from '@/hooks/use-user'
import type { ColorScheme } from '@/styles/theme/types'

import { Auth0SignOut } from './auth0-sign-out'
import { CustomSignOut } from './custom-sign-out'
import { FronteggCustomSignOut } from './frontegg-custom-sign-out'
import { FrontEggSignOut } from './frontegg-sign-out'

export interface UserPopoverProps {
	anchorEl: null | Element
	onClose?: () => void
	open: boolean
}

export function UserPopover({ anchorEl, onClose, open }: UserPopoverProps) {
	const { user } = useUser()
	const theme = useTheme()
	const { setColorScheme } = useColorScheme()
	const { settings, setSettings } = useSettings()
	const isDark = theme.palette.mode === 'dark'

	const updateColorScheme = (mode: ColorScheme): void => {
		setColorScheme(mode)
		const updatedSettings = { ...settings, colorScheme: mode }
		setPersistedSettings(updatedSettings)
		setSettings(updatedSettings)
	}

	return (
		<Popover
			anchorEl={anchorEl}
			anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
			onClose={onClose}
			open={Boolean(open)}
			slotProps={{ paper: { sx: { width: '280px' } } }}
			transformOrigin={{ horizontal: 'right', vertical: 'top' }}
		>
			<Box sx={{ p: 2 }}>
				<Typography>{user?.name}</Typography>
				<Typography color="text.secondary" variant="body2">
					{user?.email}
				</Typography>
			</Box>
			<Divider />
			<List sx={{ p: 1 }}>
				<MenuItem component={RouterLink} to={paths.dashboard.settings.account} onClick={onClose}>
					<ListItemIcon>
						<UserIcon />
					</ListItemIcon>
					Account
				</MenuItem>
				<MenuItem
					data-test="dark-mode-toggle"
					onClick={() => { updateColorScheme(isDark ? 'light' : 'dark') }}
					sx={{ display: { xs: 'flex', lg: 'none' } }}
				>
					<ListItemIcon>
						<MoonIcon />
					</ListItemIcon>
					Dark mode
					<Switch
						checked={isDark}
						edge="end"
						inputProps={{ 'aria-label': 'Dark mode' }}
						onChange={(event) => { updateColorScheme(event.target.checked ? 'dark' : 'light') }}
						onClick={(event) => { event.stopPropagation() }}
						sx={{ ml: 'auto' }}
					/>
				</MenuItem>
				{/* <MenuItem component={RouterLink} to={paths.dashboard.settings.security} onClick={onClose}>
					<ListItemIcon>
						<LockKeyIcon />
					</ListItemIcon>
					Security
				</MenuItem> */}
			</List>
			<Divider />
			<Box sx={{ p: 1 }}>
				{config.auth.strategy === AuthStrategy.CUSTOM ? <CustomSignOut /> : null}
				{config.auth.strategy === AuthStrategy.FRONTEGG_CUSTOM ? <FronteggCustomSignOut /> : null}
				{config.auth.strategy === AuthStrategy.AUTH0 ? <Auth0SignOut /> : null}
				{config.auth.strategy === AuthStrategy.FRONTEGG ? <FrontEggSignOut /> : null}
			</Box>
		</Popover>
	)
}
